import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "employeeSelect",
    "departmentFilter",
    "contractTypeFilter",
    "selectAllButton",
    "submitButton",
    "employeeCount",
    "employeeSelectContainer",
    "periodSelect",
  ];

  connect() {
    this.setupFilterListeners();
    // Initialiser le compteur d'employés avec le nombre initial du serveur
    const initialEmployeeCount = parseInt(
      this.data.get("initialEmployeeCount"),
      10,
    );
    console.log("Connect: initialEmployeeCount", initialEmployeeCount);
    this.checkEmployeeCount(initialEmployeeCount);
  }

  setupFilterListeners() {
    // Gérer les changements de filtres
    this.departmentFilterTarget.addEventListener("change", () => {
      console.log("Changement de département détecté");
      this.applyFilters();
    });

    this.contractTypeFilterTarget.addEventListener("change", () => {
      console.log("Changement de type de contrat détecté");
      this.applyFilters();
    });
  }

  checkEmployeeCount(employeeCount) {
    console.log(
      "checkEmployeeCount appelée avec employeeCount:",
      employeeCount,
    );
    // Vérifier s'il y a des employés disponibles
    const selectContainer = this.employeeSelectTarget.parentNode;

    // Supprimer les messages précédents (où qu'ils aient été placés)
    const oldMessages = this.element.querySelectorAll(
      ".empty-employees-message",
    );
    oldMessages.forEach((el) => el.remove());

    if (employeeCount === 0) {
      console.log(
        "checkEmployeeCount: employeeCount est 0. Masquage des boutons et du conteneur du select.",
      );
      // Désactiver le bouton de génération et le cacher
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.classList.add(
        "opacity-50",
        "cursor-not-allowed",
        "hidden",
      );
      this.submitButtonTarget.classList.remove("hover:bg-cta-darker");

      // Désactiver et cacher le bouton de sélection de tous les employés
      if (this.hasSelectAllButtonTarget) {
        this.selectAllButtonTarget.disabled = true;
        this.selectAllButtonTarget.classList.add(
          "opacity-50",
          "cursor-not-allowed",
          "hidden",
        );
        this.selectAllButtonTarget.classList.remove("hover:text-cta-darker");
        console.log("selectAllButtonTarget caché et désactivé.");
      }

      // Masquer complètement le conteneur du select des employés
      if (this.hasEmployeeSelectContainerTarget) {
        this.employeeSelectContainerTarget.classList.add("hidden");
        console.log("employeeSelectContainerTarget caché.");
      }

      // Ajouter un message indiquant qu'aucun employé n'est disponible
      const emptyMessage = document.createElement("div");
      emptyMessage.className =
        "mt-2 text-sm text-amber-600 empty-employees-message";
      const countElement = this.employeeCountTarget;
      if (countElement && countElement.dataset.noEmployeesMessage) {
        emptyMessage.textContent = countElement.dataset.noEmployeesMessage;
      } else {
        emptyMessage.textContent = this.t("js.payslip_generation.no_employees");
      }
      // Hors du bloc des employés, qui vient d'être masqué : placé dedans,
      // le message était invisible et le bouton grisé restait inexpliqué.
      if (this.hasEmployeeSelectContainerTarget) {
        this.employeeSelectContainerTarget.insertAdjacentElement("afterend", emptyMessage);
      } else {
        selectContainer.appendChild(emptyMessage);
      }

      // Ajouter une bordure d'alerte au select
      this.employeeSelectTarget.classList.add("border-amber-300");
      this.employeeSelectTarget.classList.remove("border-gray-300");
    } else {
      console.log(
        "checkEmployeeCount: employeeCount n'est pas 0. Affichage des boutons et du conteneur du select.",
      );
      // Activer le bouton de génération et le montrer
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.classList.remove(
        "opacity-50",
        "cursor-not-allowed",
        "hidden",
      );
      this.submitButtonTarget.classList.add("hover:bg-cta-darker");

      // Activer et montrer le bouton de sélection de tous les employés
      if (this.hasSelectAllButtonTarget) {
        this.selectAllButtonTarget.disabled = false;
        this.selectAllButtonTarget.classList.remove(
          "opacity-50",
          "cursor-not-allowed",
          "hidden",
        );
        this.selectAllButtonTarget.classList.add("hover:text-cta-darker");
        console.log("selectAllButtonTarget affiché et activé.");
      }

      // Afficher le conteneur du select des employés
      if (this.hasEmployeeSelectContainerTarget) {
        this.employeeSelectContainerTarget.classList.remove("hidden");
        console.log("employeeSelectContainerTarget affiché.");
      }

      // Rétablir la bordure normale du select
      this.employeeSelectTarget.classList.remove("border-amber-300");
      this.employeeSelectTarget.classList.add("border-gray-300");
    }
  }

  applyFilters() {
    // Récupérer les valeurs des filtres
    const departmentFilter = this.departmentFilterTarget.value;
    const contractTypeFilter = this.contractTypeFilterTarget.value;

    console.log("Application des filtres:", {
      departmentFilter,
      contractTypeFilter,
    });

    // Afficher un indicateur de chargement
    this.showLoading();

    // Construire l'URL pour la requête AJAX
    // The month too: who already has its payslip is no longer offered (QA PY-SP-07).
    const period = this.hasPeriodSelectTarget ? this.periodSelectTarget.value : "";
    const url = `/schools/payrolls/payslip_generations/filter_employees?department_filter=${departmentFilter}&contract_type_filter=${contractTypeFilter}&period=${encodeURIComponent(period)}`;

    // One request per choice: the month's select fires « change » more than
    // once, and an answer that comes late must not put an older list back.
    if (url === this.loadingUrl) return;
    this.loadingUrl = url;

    // Récupérer le token CSRF
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      .getAttribute("content");

    // Effectuer une requête AJAX pour obtenir les employés filtrés
    fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": csrfToken,
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Erreur réseau: ${response.status} ${response.statusText}`,
          );
        }
        return response.json();
      })
      .then((data) => {
        if (url !== this.loadingUrl) return;
        this.loadingUrl = null;
        console.log("Données reçues:", data);

        // Sauvegarder les valeurs sélectionnées actuelles
        const selectedValues = Array.from(
          this.employeeSelectTarget.selectedOptions,
        ).map((option) => option.value);

        // Mettre à jour le select des employés
        this.updateEmployeeSelect(data.employees, selectedValues);

        // Mettre à jour le compteur d'employés et le statut des boutons
        this.updateEmployeeCount(data.count);
        console.log(
          "applyFilters: appel de checkEmployeeCount avec data.count:",
          data.count,
        );
        this.checkEmployeeCount(data.count);

        // Masquer l'indicateur de chargement
        this.hideLoading();
      })
      .catch((error) => {
        if (url === this.loadingUrl) this.loadingUrl = null;
        console.error("Erreur lors du filtrage:", error);
        this.hideLoading();
        this.showError();
      });
  }

  showLoading() {
    // Ajouter une classe pour indiquer le chargement
    this.employeeSelectTarget.classList.add("opacity-50");
    this.employeeSelectTarget.disabled = true;
  }

  hideLoading() {
    // Retirer la classe de chargement
    this.employeeSelectTarget.classList.remove("opacity-50");
    this.employeeSelectTarget.disabled = false;
  }

  updateEmployeeSelect(employees, selectedValues) {
    const select = this.employeeSelectTarget;
    const placeholder = this.data.get("selectPlaceholder");

    // Vider le select
    select.innerHTML = "";

    // Ajouter l'option par défaut (vide) si nécessaire
    // Nous l'ajoutons toujours car la vue Rails s'attend à ce qu'elle soit présente pour l'interface utilisateur
    const defaultOption = document.createElement("option");
    defaultOption.value = "";
    defaultOption.textContent = placeholder;
    select.appendChild(defaultOption);

    // Ajouter les options pour chaque employé
    employees.forEach((employee) => {
      const option = document.createElement("option");
      option.value = employee.id;
      option.textContent = `${employee.full_name}`;
      option.selected = selectedValues.includes(employee.id.toString());
      select.appendChild(option);
    });

    // SlimSelect keeps its own list: rewriting the native options then firing
    // « change » left it on the previous month's employees, and « Sélectionner
    // tous » submitted them (QA3 SP3-A09). It is given the new list itself.
    if (select.slim && typeof select.slim.setData === "function") {
      select.slim.setData(
        Array.from(select.options).map((option) => ({
          text: option.text,
          value: option.value,
          placeholder: option.value === "",
          selected: option.selected,
        })),
      );
      return;
    }

    // Déclencher l'événement change pour mettre à jour les sélections (utile si un select multi est utilisé par un framework JS)
    const event = new Event("change", { bubbles: true });
    select.dispatchEvent(event);
  }

  updateEmployeeCount(count) {
    // Mettre à jour le compteur d'employés affiché dans la vue
    const countElement = this.employeeCountTarget;
    if (countElement) {
      countElement.textContent = countElement.dataset.countTemplate.replace(
        "%{count}",
        count,
      );
    }
  }

  selectAllEmployees(event) {
    event.preventDefault();
    const select = this.employeeSelectTarget;

    // Vérifier si des employés sont disponibles pour être sélectionnés
    // (Exclut l'option vide/placeholder)
    const actualOptions = Array.from(select.options).filter(
      (option) => option.value !== "",
    );

    if (actualOptions.length === 0) {
      console.log("Aucun employé disponible à sélectionner.");
      return; // Ne rien faire si aucun employé réel n'est présent
    }

    const allSelected = actualOptions.every((option) => option.selected);

    // SlimSelect keeps its own selection and overrides the native options:
    // it has to be told (QA PY-SP-09 — the placeholder was selected instead).
    if (select.slim) {
      select.slim.setSelected(
        allSelected ? [] : actualOptions.map((option) => option.value),
      );
      return;
    }

    actualOptions.forEach((option) => {
      option.selected = !allSelected;
    });

    // Déclencher l'événement change pour mettre à jour les sélections
    const changeEvent = new Event("change", { bubbles: true });
    select.dispatchEvent(changeEvent);
  }

  disconnect() {
    // Nettoyage si nécessaire (ex: removeEventListener si ce n'est pas géré automatiquement par Stimulus)
  }

  t(key, options = {}) {
    if (typeof window !== "undefined" && typeof window.t === "function") {
      return window.t(key, options);
    }
    // Fallback defaults
    const defaults = {
      "js.payslip_generation.no_employees":
        "Aucun employé ne correspond aux filtres sélectionnés. Veuillez modifier vos critères de filtrage.",
      "js.payslip_generation.filter_error":
        "Une erreur est survenue lors du filtrage des employés. Veuillez réessayer.",
    };
    return defaults[key] || key;
  }

  showError() {
    const selectContainer = this.employeeSelectTarget.parentNode;
    const errorMessage = document.createElement("div");
    errorMessage.className =
      "mt-2 text-sm text-red-600 empty-employees-message";
    const countElement = this.employeeCountTarget;
    if (countElement && countElement.dataset.filterErrorMessage) {
      errorMessage.textContent = countElement.dataset.filterErrorMessage;
    } else {
      errorMessage.textContent = this.t("js.payslip_generation.filter_error");
    }
    selectContainer.appendChild(errorMessage);

    this.employeeSelectTarget.classList.add("border-red-300");
    this.employeeSelectTarget.classList.remove("border-gray-300");

    this.submitButtonTarget.disabled = true;
    this.submitButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
    this.submitButtonTarget.classList.remove("hover:bg-cta-darker");
  }
}
